import React, { useCallback, useEffect, useRef, useState } from 'react'
import Container from './Container'
import GlobalContext from './contexts/Global'
import StoriesContext from './contexts/Stories'
import ProgressContext from './contexts/Progress'
import { getRenderer } from './util/renderers'
import { renderers as defaultRenderers } from './renderers/index'
import withHeader from './renderers/wrappers/withHeader'
import withSeeMore from './renderers/wrappers/withSeeMore'
import { isEqual } from 'lodash'

const PREVIOUS_RESTART_THRESHOLD = 20

/** @returns {'media' | 'timer'} */
const getStoryProgressMode = story => (story?.trackDuration ? 'media' : 'timer')

const ReactInstaStories = function (props) {
    const {
        stories: inputStories,
        renderers,
        width = 360,
        height = 640,
        defaultInterval = 4000,
        loader,
        header,
        storyStyles,
        loop,
        isPaused,
        currentIndex,
        onStoryStart,
        onStoryEnd,
        onAllStoriesEnd,
        keyboardNavigation,
    } = props
    let allRenderers = renderers ? renderers.concat(defaultRenderers) : defaultRenderers
    let context = {
        width,
        height,
        loader,
        header,
        storyStyles,
        loop,
        defaultInterval,
        isPaused,
        currentIndex,
        onStoryStart,
        onStoryEnd,
        onAllStoriesEnd,
        keyboardNavigation,
    }
    const [stories, setStories] = useState({ stories: generateStories(inputStories, allRenderers) })
    const [currentId, setCurrentId] = useState(0)
    const [pause, setPause] = useState(Boolean(isPaused))
    const [videoDuration, setVideoDuration] = useState(0)
    const [count, setCount] = useState(0)
    const [progressMode, setProgressMode] = useState(
        getStoryProgressMode(generateStories(inputStories, allRenderers)[0]),
    )
    const [storyCycle, setStoryCycle] = useState(0)
    const [playbackCommand, setPlaybackCommand] = useState({
        action: Boolean(isPaused) ? 'pause' : 'play',
        source: 'initial',
        sequence: 0,
    })
    const currentIdRef = useRef(currentId)
    const storiesRef = useRef(stories.stories)

    useEffect(() => {
        currentIdRef.current = currentId
    }, [currentId])

    useEffect(() => {
        storiesRef.current = stories.stories
    }, [stories])

    const toggleState = useCallback((action, source = 'runtime') => {
        setPause(action === 'pause')
        setPlaybackCommand(prev => ({
            action,
            source,
            sequence: prev.sequence + 1,
        }))
    }, [])

    const resetStoryRuntime = useCallback(
        nextStory => {
            setCount(0)
            setVideoDuration(0)
            setProgressMode(getStoryProgressMode(nextStory))
            setStoryCycle(prev => prev + 1)
        },
        [setCount, setVideoDuration],
    )

    const goToStory = useCallback(
        nextIndex => {
            const storiesList = storiesRef.current || []
            const boundedIndex = Math.max(0, Math.min(storiesList.length - 1, nextIndex))
            resetStoryRuntime(storiesList[boundedIndex])
            setCurrentId(boundedIndex)
        },
        [resetStoryRuntime],
    )

    const restartCurrentStory = useCallback(() => {
        const storiesList = storiesRef.current || []
        resetStoryRuntime(storiesList[currentIdRef.current])
    }, [resetStoryRuntime])

    const finishStories = useCallback(() => {
        setPause(true)
        setPlaybackCommand(prev => ({
            action: 'pause',
            source: 'runtime',
            sequence: prev.sequence + 1,
        }))
        onAllStoriesEnd?.(currentIdRef.current, storiesRef.current)
    }, [onAllStoriesEnd])

    const previous = useCallback(() => {
        const currentIndex = currentIdRef.current
        if (count > PREVIOUS_RESTART_THRESHOLD || currentIndex === 0) {
            restartCurrentStory()
            return
        }

        goToStory(currentIndex - 1)
    }, [count, goToStory, restartCurrentStory])

    const next = useCallback(() => {
        const storiesList = storiesRef.current || []
        const currentIndex = currentIdRef.current

        if (!storiesList.length) return

        if (loop) {
            goToStory((currentIndex + 1) % storiesList.length)
            return
        }

        if (currentIndex >= storiesList.length - 1) {
            finishStories()
            return
        }

        goToStory(currentIndex + 1)
    }, [finishStories, goToStory, loop])

    useEffect(() => {
        const generatedStories = generateStories(inputStories, allRenderers)
        setStories({ stories: generatedStories })

        setCurrentId(prev => Math.max(0, Math.min(generatedStories.length - 1, prev)))
        setProgressMode(getStoryProgressMode(generatedStories[currentIdRef.current]))
    }, [inputStories, allRenderers])

    useEffect(() => {
        if (typeof currentIndex !== 'number') return
        const storiesList = storiesRef.current || []
        if (!storiesList.length) return
        if (currentIndex < 0 || currentIndex >= storiesList.length) return

        goToStory(currentIndex)
    }, [currentIndex, goToStory])

    useEffect(() => {
        if (typeof isPaused === 'boolean') {
            setPause(isPaused)
        }
    }, [isPaused])

    return (
        <GlobalContext.Provider value={context}>
            <StoriesContext.Provider value={stories}>
                <ProgressContext.Provider
                    value={{
                        videoDuration: videoDuration || 0,
                        currentId,
                        pause,
                        next,
                        setVideoDuration,
                        setCurrentId,
                        setPause,
                        previous,
                        toggleState,
                        count,
                        setCount,
                        playbackCommand,
                        progressMode,
                        setProgressMode,
                        storyCycle,
                    }}
                >
                    <Container />
                </ProgressContext.Provider>
            </StoriesContext.Provider>
        </GlobalContext.Provider>
    )
}
const generateStories = (stories, renderers) => {
    return stories.map(s => {
        let story = {}
        if (typeof s === 'string') {
            story.url = s
            story.type = 'image'
        } else if (typeof s === 'object') {
            story = Object.assign(story, s)
        }
        let renderer = getRenderer(story, renderers)
        story.originalContent = story.content
        story.content = renderer
        return story
    })
}
export const WithHeader = withHeader
export const WithSeeMore = withSeeMore
export default ReactInstaStories
